import { PickerForm } from '../../base/PickerForm/PickerForm';
import { useCourseTeams, useLearningPathTeams, useUsers } from '../../../../lib/api/swrApi';
import { filterUsers, isUser, renderUser, userIdFn } from './CourseAssignment.utils';
import { ASSIGN_COURSE_LABELS, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import { useAssignTeamToTraining, useAssignUserToTraining } from '../../../../lib/api/postApi';
import { PickerFormItem } from '../../base/PickerForm/PickerFormTypes';
import { replacePlaceholder } from '../../../../lib/utils/placeholders/replacePlaceholder';
import { CourseAssignmentModalProps } from './CourseAssignment.types';
import { ERROR_MESSAGES_SPLITTER } from '../../../../lib/constants/globals';
import { filterTeams, renderTeam, teamIdFunction } from '../../base/PickerForm/PickerForm.utils';
import { isCourseType, isLearningPathType } from '../../../../lib/utils/trainings/typesChecking';
import { isPromiseFullfiled, isPromiseRejected } from '../../../../lib/utils/promises';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

const CourseAssignmentModal = ({
  noItemsSelectedMessage,
  dialogTitle,
  isModalOpened,
  onCancel,
  onSuccess,
  courseDetails,
  type,
  teamSizeWarningMessage,
}: CourseAssignmentModalProps) => {
  const { id } = courseDetails;
  const { getParsedErrorMessage } = useErrorMapping();

  const {
    data: usersData,
    mutate: mutateUsers,
    isLoading: isUsersLoading,
    error: usersError,
  } = useUsers({ training: { id, type } });

  const {
    data: lpData,
    mutate: mutateLpTeams,
    isLoading: isLpTeamsLoading,
    error: lpTeamsError,
  } = useLearningPathTeams(id, isLearningPathType(type));

  const {
    data: courseData,
    mutate: mutateCourseTeams,
    isLoading: isCourseTeamsLoading,
    error: courseTeamsError,
  } = useCourseTeams(id, isCourseType(type));

  const assignTeam = useAssignTeamToTraining(type, id);
  const assignUser = useAssignUserToTraining(type);

  const handleMutateData = async () => Promise.allSettled([mutateUsers(), mutateLpTeams(), mutateCourseTeams()]);

  const handleSubmit = async (selectedItems: PickerFormItem[]) => {
    const results = await Promise.allSettled(
      selectedItems.map(selectedItem => {
        if (isUser(selectedItem)) {
          return assignUser(selectedItem.userId, id);
        }

        return assignTeam(selectedItem.id);
      }),
    );

    if (results.some(isPromiseFullfiled)) {
      handleMutateData();
    }

    const rejectedResults = results.filter(isPromiseRejected);
    if (rejectedResults.length) {
      onSuccess(false);

      throw new Error(rejectedResults.map(item => getParsedErrorMessage(item.reason)).join(ERROR_MESSAGES_SPLITTER));
    }

    onSuccess();
  };

  return (
    <PickerForm
      loading={isUsersLoading || (isLearningPathType(type) ? isLpTeamsLoading : isCourseTeamsLoading)}
      error={usersError ?? (isLearningPathType(type) ? lpTeamsError : courseTeamsError)}
      value={[
        {
          titleKey: COMMON_LABELS.TAB.TEAMS,
          list: [...(courseData ?? []), ...(lpData ?? [])],
          render: renderTeam,
          filterFunction: filterTeams,
          idFunction: teamIdFunction,
          searchPlaceholderKey: COMMON_LABELS.SEARCH.TEAM,
        },
        {
          titleKey: COMMON_LABELS.TAB.USERS,
          list: usersData?.map(user => ({ ...user, id: user.userId })) ?? [],
          render: renderUser,
          filterFunction: filterUsers,
          idFunction: userIdFn,
          searchPlaceholderKey: COMMON_LABELS.SEARCH.USER,
        },
      ]}
      onSubmit={handleSubmit}
      labels={{
        selectedItemsHeadingKey: COMMON_LABELS.DIALOG.SELECTED_ITEMS,
        noItemsSelectedMessage: replacePlaceholder(courseDetails, noItemsSelectedMessage),
        dialogTitle: replacePlaceholder(courseDetails, dialogTitle),
        submitButtonLabelKey: ASSIGN_COURSE_LABELS.ASSIGN_BUTTON,
        assignedSummaryKey: ASSIGN_COURSE_LABELS.ASSIGNED_SUMMARY,
      }}
      teamSizeWarningMessage={teamSizeWarningMessage}
      isOpen={isModalOpened}
      onCancel={onCancel}
    />
  );
};

export default CourseAssignmentModal;
